import { Page, View, Text, Image } from '@react-pdf/renderer';
import { StyleSheet } from '@react-pdf/renderer';
import { LaudoHeader, VistoriaImovel, Footer, QuestionarioItem } from '../../../types';
import { AVMPageFooter } from '../AVM/components';
import { AnswerRenderer } from './AnswerRenderer';

const s = StyleSheet.create({
  page: { width: 595, height: 842, backgroundColor: '#FFFFFF', flexDirection: 'column', fontFamily: 'Helvetica', paddingBottom: 40 },
  headerBar: { paddingBottom: 8, borderBottom: '1 solid #D6DBE7', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  sectionHeader: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#F5F6F9', gap: 8, paddingRight: 8 },
  accentBlue: { width: 3, height: 28, backgroundColor: '#0090FF' },
  subHeader: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#F5F6F9', gap: 8, paddingRight: 8 },
  accentDark: { width: 3, height: 28, backgroundColor: '#101010' },
  title: { color: '#000000', fontSize: 9, fontWeight: 'bold' },
  dataRow: { flexDirection: 'row', paddingHorizontal: 8, paddingVertical: 6, borderBottom: '1 solid #D6DBE7', overflow: 'hidden' },
  label: { color: '#000000', fontSize: 7.5, fontWeight: 'normal' },
  value: { color: '#000000', fontSize: 9, fontWeight: 'bold' },
});

interface ChecklistProps {
  header: LaudoHeader;
  vistoriaImovel: VistoriaImovel;
  footer: Footer;
  logoBase64?: string;
}

const COMPLEX_ANSWER_TYPES = ['File', 'Table', 'Photo'];

function formatAnswer(item: QuestionarioItem): string {
  switch (item.answerType) {
    case 'Currency':
      return item.valor !== undefined ? `R$ ${item.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : '-';
    case 'NumericInteger':
    case 'NumericDecimal':
      return item.valor !== undefined ? item.valor.toString() : '-';
    case 'Date':
      return item.data ? new Date(item.data).toLocaleDateString('pt-BR') : '-';
    case 'ShortAnswer':
    case 'LongAnswer':
    case 'YesNo':
    case 'RadioButton':
      return item.resposta || '-';
    case 'Checkbox':
      return item.respostas?.join(' · ') || '-';
    default:
      return '-';
  }
}

function isSimple(item: QuestionarioItem): boolean {
  if (COMPLEX_ANSWER_TYPES.includes(item.answerType)) return false;
  return formatAnswer(item).length < 80;
}

function groupBySection(items: QuestionarioItem[]): { name: string; items: QuestionarioItem[] }[] {
  const groups: Map<string, QuestionarioItem[]> = new Map();
  for (const item of items) {
    const key = item.sectionName || '';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(item);
  }
  const result: { name: string; items: QuestionarioItem[] }[] = [];
  for (const [name, groupItems] of groups) {
    result.push({ name, items: groupItems });
  }
  result.sort((a, b) => (a.items[0]?.sectionOrder ?? 999) - (b.items[0]?.sectionOrder ?? 999));
  return result;
}

// Build a flat list of "renderable blocks" so we can control wrap behavior
type Block =
  | { type: 'subheader'; name: string; isFirst: boolean }
  | { type: 'gridrow'; items: QuestionarioItem[]; getIdx: (item: QuestionarioItem) => string }
  | { type: 'complex'; item: QuestionarioItem; idx: string };

function buildBlocks(sections: { name: string; items: QuestionarioItem[] }[], hasSections: boolean): Block[] {
  const blocks: Block[] = [];
  let globalIdx = 0;

  for (let si = 0; si < sections.length; si++) {
    const section = sections[si];
    const sectionStartIdx = globalIdx;

    const getIdx = (item: QuestionarioItem) => {
      const idx = section.items.indexOf(item);
      return String(sectionStartIdx + idx + 1).padStart(2, '0');
    };

    if (hasSections && section.name) {
      blocks.push({ type: 'subheader', name: section.name.toUpperCase(), isFirst: si === 0 });
    }

    // Process items in order, grouping consecutive simple items into rows of 3
    let simpleBuffer: QuestionarioItem[] = [];

    const flushSimple = () => {
      for (let i = 0; i < simpleBuffer.length; i += 3) {
        blocks.push({ type: 'gridrow', items: simpleBuffer.slice(i, i + 3), getIdx });
      }
      simpleBuffer = [];
    };

    for (const item of section.items) {
      if (isSimple(item)) {
        simpleBuffer.push(item);
      } else {
        flushSimple();
        blocks.push({ type: 'complex', item, idx: getIdx(item) });
      }
    }
    flushSimple();

    globalIdx += section.items.length;
  }

  return blocks;
}

function GridRowBlock({ items, getIdx }: { items: QuestionarioItem[]; getIdx: (item: QuestionarioItem) => string }) {
  return (
    <View style={s.dataRow}>
      {items.map((item, ci) => (
        <View key={ci} style={{ flex: 1, flexDirection: 'column', gap: 2, overflow: 'hidden' }}>
          <Text style={s.label}>{getIdx(item)} - {item.pergunta}</Text>
          <Text style={s.value}>{formatAnswer(item)}</Text>
          {item.notes && item.notes.trim() && (
            <Text style={{ fontSize: 7, color: '#6B7280', fontStyle: 'italic', marginTop: 1 }}>
              Justificativa: {item.notes}
            </Text>
          )}
        </View>
      ))}
      {items.length < 3 && [...Array(3 - items.length)].map((_, i) => (
        <View key={`e-${i}`} style={{ flex: 1 }} />
      ))}
    </View>
  );
}

function ComplexBlock({ item, idx }: { item: QuestionarioItem; idx: string }) {
  const hasNotes = item.notes && item.notes.trim();
  return (
    <View style={{ paddingHorizontal: 8, paddingVertical: 6, borderBottom: '1 solid #D6DBE7' }}>
      <Text style={s.label}>{idx} - {item.pergunta}</Text>
      <AnswerRenderer item={item} />
      {hasNotes && (
        <Text style={{ fontSize: 7, color: '#6B7280', fontStyle: 'italic', marginTop: 2 }}>
          Justificativa: {item.notes}
        </Text>
      )}
    </View>
  );
}

function SubHeaderBlock({ name, isFirst }: { name: string; isFirst: boolean }) {
  return (
    <View style={isFirst ? s.subHeader : [s.subHeader, { marginTop: 12 }]}>
      <View style={s.accentDark} />
      <Text style={s.title}>{name}</Text>
    </View>
  );
}

export function ChecklistBody({ vistoriaImovel }: { vistoriaImovel: VistoriaImovel }) {
  if (!vistoriaImovel.questionario || vistoriaImovel.questionario.length === 0) return null;

  const itensSemFoto = vistoriaImovel.questionario.filter(
    item => item.answerType !== 'Photo'
  );

  if (itensSemFoto.length === 0) return null;

  const sections = groupBySection(itensSemFoto);
  const hasSections = sections.length > 1 || (sections.length === 1 && sections[0].name !== '');
  const blocks = buildBlocks(sections, hasSections);

  return (
    <View style={{ flexDirection: 'column' }}>
      {/* Title "QUESTIONÁRIO" inline (não fixed) */}
      <View style={s.sectionHeader} wrap={false}>
        <View style={s.accentBlue} />
        <Text style={s.title}>QUESTIONÁRIO</Text>
      </View>

      {/* Content */}
      <View style={{ paddingTop: 8, flexDirection: 'column' }}>
        {blocks.map((block, i) => {
          // If this is a subheader, glue it to the next block using wrap={false}
          if (block.type === 'subheader') {
            const next = blocks[i + 1];
            return (
              <View key={i} wrap={false}>
                <SubHeaderBlock name={block.name} isFirst={block.isFirst} />
                {next && next.type === 'gridrow' && (
                  <GridRowBlock items={next.items} getIdx={next.getIdx} />
                )}
                {next && next.type === 'complex' && (
                  <ComplexBlock item={next.item} idx={next.idx} />
                )}
              </View>
            );
          }

          const prev = blocks[i - 1];
          if (prev && prev.type === 'subheader') return null;

          if (block.type === 'gridrow') {
            return <View key={i} wrap={false}><GridRowBlock items={block.items} getIdx={block.getIdx} /></View>;
          }
          if (block.type === 'complex') {
            return <View key={i} wrap={false}><ComplexBlock item={block.item} idx={block.idx} /></View>;
          }

          return null;
        })}
      </View>
    </View>
  );
}

export function Checklist({ header, vistoriaImovel, footer, logoBase64 }: ChecklistProps) {
  if (!vistoriaImovel.questionario || vistoriaImovel.questionario.length === 0) return null;

  const itensSemFoto = vistoriaImovel.questionario.filter(
    item => item.answerType !== 'Photo'
  );

  if (itensSemFoto.length === 0) return null;

  const sections = groupBySection(itensSemFoto);
  const hasSections = sections.length > 1 || (sections.length === 1 && sections[0].name !== '');
  const blocks = buildBlocks(sections, hasSections);

  return (
    <Page size="A4" style={s.page}>
      {/* Fixed header that repeats on every page */}
      <View fixed style={{ paddingHorizontal: 16, paddingTop: 16 }}>
        <View style={s.headerBar}>
          {logoBase64 ? (
            <Image src={logoBase64} style={{ width: 64, height: 24, objectFit: 'contain' }} />
          ) : (
            <Text style={{ fontSize: 16, fontWeight: 'bold', color: '#101010' }}>bari.</Text>
          )}
          <View style={{ flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
            <Text style={{ textAlign: 'right', color: '#000000', fontSize: 10, fontWeight: 'bold' }}>LAUDO DE AVALIAÇÃO</Text>
            <Text style={{ textAlign: 'right', color: '#000000', fontSize: 9 }}>{header.idLaudo}  ·  {header.formatoLaudo}</Text>
          </View>
        </View>
        <View style={[s.sectionHeader, { marginTop: 12 }]}>
          <View style={s.accentBlue} />
          <Text style={s.title}>QUESTIONÁRIO</Text>
        </View>
      </View>

      {/* Content */}
      <View style={{ paddingHorizontal: 16, paddingTop: 8, flexDirection: 'column' }}>
        {blocks.map((block, i) => {
          // If this is a subheader, glue it to the next block using wrap={false}
          if (block.type === 'subheader') {
            const next = blocks[i + 1];
            return (
              <View key={i} wrap={false}>
                <SubHeaderBlock name={block.name} isFirst={block.isFirst} />
                {/* Render the next block inside this wrap={false} container */}
                {next && next.type === 'gridrow' && (
                  <GridRowBlock items={next.items} getIdx={next.getIdx} />
                )}
                {next && next.type === 'complex' && (
                  <ComplexBlock item={next.item} idx={next.idx} />
                )}
              </View>
            );
          }

          // Skip blocks already rendered as part of a subheader group
          const prev = blocks[i - 1];
          if (prev && prev.type === 'subheader') return null;

          // Regular blocks
          if (block.type === 'gridrow') {
            return <View key={i} wrap={false}><GridRowBlock items={block.items} getIdx={block.getIdx} /></View>;
          }
          if (block.type === 'complex') {
            return <View key={i} wrap={false}><ComplexBlock item={block.item} idx={block.idx} /></View>;
          }

          return null;
        })}
      </View>

      <AVMPageFooter footer={footer} />
    </Page>
  );
}
